import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import MainDialogBox from '../components/Dialog/DialogMainBox';
import { Constants } from '../constants';
import { ImgText } from './Heading';
import { Icon } from '@iconify/react/dist/iconify.js';
import { CustomButtonOutline } from './CustomButton';

const ToasterContext = createContext(null);

export const ToasterProvider = ({ children }) => {
    const [open, setOpen] = useState({
        success: false,
        error: false,
        warning: false
    });

    const [heading, setHeading] = useState('');
    const [subHeading, setSubHeading] = useState('');
    const [type, setType] = useState('success');
    const [linkConfig, setLinkConfig] = useState({ showLink: false, linkText: '', onClickLink: null });
    const [isAutoClose, setIsAutoClose] = useState(true);
    
    // Ref to store timeout IDs
    const timeoutRef = useRef(null);

    // Auto-close timer
    useEffect(() => {
        // Clear any existing timeout when component unmounts or when open state changes
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const showToaster = (newHeading, newSubHeading, toasterType, options = {}) => {
        setHeading(newHeading);
        setSubHeading(newSubHeading);
        setType(toasterType);
        setLinkConfig({
            showLink: options?.showLink || false,
            linkText: options?.linkText || 'Tap to view full details',
            onClickLink: options?.onClickLink || null
        });
        setIsAutoClose(options?.autoClose ?? true);

        setOpen(prev => ({
            ...prev,
            [toasterType]: true
        }));

        const duration = options?.duration || 1000;

        // Auto-close after duration
        if (options?.autoClose ?? true) {
            timeoutRef.current = setTimeout(() => {
                handleClose(toasterType);
                timeoutRef.current = null;
            }, duration);
        }
    };

    const handleClose = (toasterType) => {
        setOpen(prev => ({
            ...prev,
            [toasterType]: false
        }));
    };

    return (
        <ToasterContext.Provider
            value={{
                showToaster,
                handleClose,
                open,
                type
            }}
        >
            {children}

            {open.success && (
                <MainDialogBox
                    open={open.success}
                    handleClose={() => handleClose('success')}
                    width={'40%'}
                    padding={'0 10px 10px 10px'}
                    isShowCross={!isAutoClose}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            backgroundColor: 'green.100'
                        }}
                    >
                        <img
                            src={Constants.customImages.CompletedImg}
                            alt='Success'
                            width={'40%'}
                        />

                        <ImgText
                            text={heading}
                            sx={{
                                textAlign: 'center',
                                fontSize: '20px',
                                mt: 1,
                                color: 'green'
                            }}
                        />

                        <Typography
                            variant='body2'
                            color='grey.100'
                            my={2}
                            textAlign={'center'}
                        >
                            {subHeading}
                        </Typography>
                        {linkConfig.showLink && (
                            <Typography
                                variant="body2"
                                fontSize={14}
                                fontWeight={500}
                                color="#2A49F7"
                                sx={{ cursor: "pointer", display: "flex", alignItems: "center", mb: 2 }}
                                onClick={linkConfig.onClickLink}
                            >
                                {linkConfig.linkText}
                            </Typography>
                        )}
                        {!isAutoClose && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mb: 3
                                }}
                            >
                                <CustomButtonOutline
                                    buttonText="Close"
                                    onClick={() => handleClose('success')}
                                />
                            </Box>
                        )}
                    </Box>
                </MainDialogBox>
            )}

            {open.error && (
                <MainDialogBox
                    open={open.error}
                    handleClose={() => handleClose('error')}
                    width={'40%'}
                    padding={0}
                    isShowCross={false}
                >
                    <Box sx={{ bgcolor: "#DC143C", px: 3, py: 5 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Typography variant='h2' color='#fff'>
                                Error
                            </Typography>

                            <Icon
                                icon="solar:danger-bold"
                                color='#fff'
                                width="30"
                                height="30"
                            />
                        </Box>

                        <Typography
                            variant='body1'
                            color='#fff'
                            mt={2}
                        >
                            {subHeading}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            my: 3
                        }}
                    >
                        <CustomButtonOutline
                            buttonText="Close"
                            onClick={() => handleClose('error')}
                        />
                    </Box>
                </MainDialogBox>
            )}

            {open.warning && (
                <MainDialogBox
                    open={open.warning}
                    handleClose={() => handleClose('warning')}
                    width={'35%'}
                    padding={0}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: 'center',
                            bgcolor: "#FFC107",
                            height: "150px"
                        }}
                    >
                        <Icon
                            icon="material-symbols:warning-rounded"
                            color='#fff'
                            width="60"
                            height="60"
                        />
                    </Box>

                    <Box px={3} pb={3}>
                        <Typography
                            variant='h2'
                            my={2}
                            textAlign={"center"}
                        >
                            {heading}
                        </Typography>

                        <Typography
                            variant='body2'
                            color='grey.1000'
                            textAlign={"center"}
                        >
                            {subHeading}
                        </Typography>
                    </Box>
                </MainDialogBox>
            )}
        </ToasterContext.Provider>
    );
};

export const useToaster = () => {
    const context = useContext(ToasterContext);

    if (!context) {
        throw new Error('useToaster must be used within ToasterProvider');
    }

    return context;
};